import { useGesture } from '@use-gesture/react';
import clsx from 'clsx';
import type { NumberValue, ScaleBand } from 'd3-scale';
import { useRef } from 'react';

import { axisContainerStyle } from './AxisArea.sty.js';
import { computeCollisionAwareMaxSpace } from './utils/compute-collision-aware-max-space.js';
import { isAxisPannable } from './utils/is-axis-pannable.js';
import { XTickWithTruncation } from './XTickWithTruncation.js';
import { XYChartPanningControls } from './XYChartPanningControls.js';
import { AxisTop } from '../../../core/components/axes/AxisTop.js';
import { DivAxisWrapper } from '../../../core/components/axes/components/DivAxisWrapper.js';
import {
  axisFontProps,
  EMOJI_REGEX,
} from '../../../core/components/axes/constants.js';
import { getXTickMaxSize } from '../../../core/components/axes/utils/calculate-axis-size.js';
import { getXAxisTickLabelProps } from '../../../core/components/axes/utils/get-tick-label-props.js';
import { isTickLabelSpaceAvailable } from '../../../core/components/axes/utils/is-tick-label-visible.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { KEYBOARD_HANDLERS } from '../../../core/components/zoom/constants.js';
import { cursorStyle } from '../../../core/components/zoom/ZoomCursors.sty.js';
import { XY_CHART_TOP_AXIS_LABEL } from '../../constants/defaults.js';
import { useAxesTicks } from '../../hooks/useAxesTicks.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { useXYChartScales } from '../../hooks/useXYChartScales.js';
import { isCategorical } from '../../types/guards.js';
import { AREAS } from '../../utils/grid-templates.js';
import { useApplyPanDelta } from '../overlay/hooks/useApplyPanDelta.js';

export function XYChartTopAxisArea() {
  const axisRef = useRef<HTMLDivElement>(null);

  const { topTicks } = useAxesTicks();
  const { top: topConfig } = useVisibleAxesConfig();
  const { top: topScale } = useVisibleAxesScales();

  const scales = useXYChartScales();

  const canPan = isAxisPannable(topConfig, scales);

  const tickFormat = (_: unknown, index: number) =>
    topTicks.formattedTicks[index];

  const isCategoricalAxis = topConfig?.type
    ? isCategorical(topConfig.type)
    : false;
  const isTickRotated = topConfig?.tickLabelLayout === 'vertical';
  const tickLabelProps = getXAxisTickLabelProps(
    'top',
    isCategoricalAxis,
    isTickRotated,
    topScale,
    topConfig?.tickLabelLayout,
  );

  const areCategoricalTicksNotFittable =
    topScale &&
    isCategoricalAxis &&
    !isTickLabelSpaceAvailable((topScale as ScaleBand<string>).step());

  const hideAxis =
    topConfig === undefined ||
    areCategoricalTicksNotFittable ||
    topScale === undefined;

  const handlePan = useApplyPanDelta();

  useGesture(
    {
      onDrag: (e) =>
        KEYBOARD_HANDLERS.includes(e.type) ? null : handlePan(-e.delta[0]),
    },
    {
      target: axisRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true, pointer: { buttons: [1, 4] } },
      enabled: canPan,
    },
  );

  const tickNameHasEmoji = topTicks.ticks.some((name) =>
    EMOJI_REGEX.test(name),
  );

  return hideAxis ? null : (
    <ChartGrid.GridArea gridArea={AREAS.topAxis}>
      {({ availableWidth, availableHeight }) => {
        const maxSpaceForTruncation = computeCollisionAwareMaxSpace(
          getXTickMaxSize(
            availableHeight,
            availableWidth,
            isTickRotated,
            topScale,
            topConfig.label,
            topTicks.ticks.length,
          ),
          topTicks.formattedTicks,
          topTicks.ticks as unknown as NumberValue[],
          topScale,
          isCategoricalAxis,
          isTickRotated,
        );

        return (
          <div
            className={clsx(
              cursorStyle({ isPanEnabled: canPan }),
              axisContainerStyle,
            )}
            ref={axisRef}
          >
            {canPan && (
              <XYChartPanningControls
                visibleAxis={topConfig}
                onClickPan={(delta) => handlePan(-delta)}
              />
            )}
            <DivAxisWrapper
              divAxisStyle={axisFontProps}
              ticks={topTicks.formattedTicks}
              label={topConfig.label}
              isTickVertical
              isTickRotated={isTickRotated}
            >
              <AxisTop
                role="group"
                aria-label={XY_CHART_TOP_AXIS_LABEL}
                scale={topScale}
                containerSize={availableWidth}
                height={availableHeight}
                tickValues={topTicks.ticks}
                tickLabelProps={tickLabelProps}
                tickFormat={tickFormat}
                label={topConfig.label}
                tickComponent={(renderProps) => (
                  <XTickWithTruncation
                    renderProps={renderProps}
                    maxSpaceForTruncation={maxSpaceForTruncation}
                    centerTick={isCategoricalAxis}
                    isTickRotated={isTickRotated}
                    position="top"
                    tickNameHasEmoji={tickNameHasEmoji}
                  />
                )}
              />
            </DivAxisWrapper>
          </div>
        );
      }}
    </ChartGrid.GridArea>
  );
}

XYChartTopAxisArea['displayName'] = 'XYChartTopAxisArea';
